<template>
  <div class="app-sunset-wrapper">
    <div class="app-sunset-content">
      <div class="app-sunset-intro-image">
        <v-img
          :src="require('@/assets/migration/nauta-migration-intro.png')"
          :lazy-src="require('@/assets/migration/nauta-migration-intro-lazy.png')"
          :max-width="$vuetify.breakpoint.xs ? 240 : 320"
          contain
        />
      </div>

      <h1 class="app-sunset-title">
        {{ $t("appSunset.title") }}
      </h1>

      <p class="app-sunset-description">
        {{ $t("appSunset.description") }}
      </p>

      <div class="app-sunset-hero-buttons">
        <div class="app-sunset-main-button">
          <NewButton
            :text="$t('appSunset.goToNauta')"
            @on-click="displayAppSunsetDownloadModal = true"
          />
        </div>
        <div class="app-sunset-main-button">
          <NewButton
            buttonClass="black-outlined"
            :text="$t('appSunset.help')"
            @on-click="displayAppSunsetHelpModal = true"
          />
        </div>
      </div>

      <h2 class="app-sunset-section-title app-sunset-left-title">
        {{ $t("appSunset.aboutMigration") }}
      </h2>

      <div class="app-sunset-statements">
        <div
          v-for="statement in statements"
          :key="statement.key"
          class="app-sunset-statement"
        >
          <div class="app-sunset-statement-icon-container">
            <v-img
              :src="statement.icon"
              max-width="33"
              contain
            />
          </div>
          <div class="app-sunset-statement-text-container">
            <h3>{{ $t(`appSunset.statements.${statement.key}.title`) }}</h3>
            <p>{{ $t(`appSunset.statements.${statement.key}.text`) }}</p>
          </div>
        </div>
      </div>

      <section class="app-sunset-long-text-section">
        <h2 class="app-sunset-section-title app-sunset-left-title">
          {{ $t("appSunset.whyThisChange.title") }}
        </h2>
        <p class="app-sunset-long-text">
          {{ $t("appSunset.whyThisChange.text") }}
        </p>
      </section>

      <section class="app-sunset-long-text-section">
        <h2 class="app-sunset-section-title app-sunset-left-title">
          {{ $t("appSunset.whatIsNewInNauta.title") }}
        </h2>
        <p
          class="app-sunset-long-text"
          v-html="$t('appSunset.whatIsNewInNauta.text')"
        />
      </section>

      <section class="app-sunset-more-questions">
        <h2 class="app-sunset-section-title app-sunset-left-title">
          {{ $t("appSunset.moreQuestions") }}
        </h2>
        <div class="app-sunset-whatsapp-button">
          <NewButton
            link="https://wa.me/+56982128541?text=Hola,%20necesito%20ayuda%20para%20migrarme%20a%20Nauta."
            :text="$t('appSunset.chatWhatsApp')"
            icon="mdi-whatsapp"
          />
        </div>
      </section>
    </div>

    <AppSunsetDownloadModal
      :value="displayAppSunsetDownloadModal"
      @display="displayAppSunsetDownloadModal = $event"
    />
    <AppSunsetHelpModal
      :value="displayAppSunsetHelpModal"
      @display="displayAppSunsetHelpModal = $event"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import NewButton from "@/components/shared/NewButton.vue";
import AppSunsetDownloadModal from "@/components/migration/AppSunsetDownloadModal.vue";
import AppSunsetHelpModal from "@/components/migration/AppSunsetHelpModal.vue";
import Verified from "@/assets/migration/verified.svg";
import Widgets from "@/assets/migration/widgets.svg";
import CallSplit from "@/assets/migration/call-split.svg";
import CheckboxBlankCircle from "@/assets/migration/checkbox-blank-circle.svg";
import Stream from "@/assets/migration/stream.svg";
import AccountCircle from "@/assets/migration/account-circle.svg";

export default {
  name: "AppSunset",

  components: {
    NewButton,
    AppSunsetDownloadModal,
    AppSunsetHelpModal,
  },

  data() {
    return {
      displayAppSunsetDownloadModal: false,
      displayAppSunsetHelpModal: false,
    };
  },

  mounted() {
    const customerSessionKeys = [
      "userId",
      "userName",
      "userLastName",
      "userEmail",
      "userIdentifier",
      "profile",
      "availableProfiles",
      "credentials_access_token",
      "credentials_expires_at",
      "social_access_token",
      "social_expires_at",
      "vuex",
    ];

    customerSessionKeys.forEach(key => localStorage.removeItem(key));

    if (this.$intercom?.shutdown) {
      this.$intercom.shutdown();
    }

    const recaptchaBadge = document.querySelector(".grecaptcha-badge");
    if (recaptchaBadge) {
      recaptchaBadge.remove();
    }

    this.cleanStore();
  },

  methods: {
    ...mapActions(["cleanStore"]),
  },

  computed: {
    statements() {
      return [
        {
          key: "noImpactOnInvestments",
          icon: Verified,
        },
        {
          key: "organizePortfolios",
          icon: Widgets,
        },
        {
          key: "simplifiedInvestment",
          icon: CallSplit,
        },
        {
          key: "fundAsPortfolio",
          icon: CheckboxBlankCircle,
        },
        {
          key: "personalizedRecommendation",
          icon: Stream,
        },
        {
          key: "advisoryMeetings",
          icon: AccountCircle,
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.app-sunset-wrapper {
  display: flex;
  justify-content: center;
  margin: 40px 20px;

  @media (max-width: 600px) {
    margin: 0;
  }
}

.app-sunset-content {
  max-width: 620px;
  width: 100%;

  @media (max-width: 600px) {
    max-width: none;
    padding: 44px 24px;
  }
}

.app-sunset-intro-image {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 48px;
  margin-bottom: 16px;

  @media (max-width: 960px) {
    margin-top: 32px;
  }

  @media (max-width: 600px) {
    margin-top: 0;
  }
}

.app-sunset-title {
  font-size: 40px;
  font-weight: 600;
  line-height: 110%;
  letter-spacing: -0.8px;
  color: #333333;
  text-align: center;
  margin: 0;
}

.app-sunset-description {
  margin-top: 16px;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 160%;
  color: #333333;
}

.app-sunset-hero-buttons {
  margin: 48px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  @media (max-width: 600px) {
    margin: 48px 0 80px;
  }
}

.app-sunset-main-button {
  width: 420px;
  max-width: 100%;
}

.app-sunset-section-title {
  color: #333333;
  font-size: 28px;
  line-height: 110%;
  letter-spacing: -0.56px;
  font-weight: 600;
  text-align: left;
  margin: 24px 0;
}

.app-sunset-left-title {
  text-align: left;
}

.app-sunset-statements > :last-child {
  border-bottom: none;
}

.app-sunset-statement {
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 0 18px;
  padding: 32px 0;
  border-bottom: 1px solid #e2e0e0;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 18px;
    padding: 24px 0;
  }
}

.app-sunset-statement-icon-container {
  background-color: #9378FF;
  border-radius: 12.964px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 64px;
  min-width: 64px;
}

.app-sunset-statement-text-container {
  h3 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    line-height: 130%;
    color: #333333;
  }

  p {
    margin: 6px 0 0 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 160%;
    color: #646363;
  }
}

.app-sunset-long-text-section {
  margin-top: 56px;

  @media (max-width: 600px) {
    margin-top: 80px;
  }
}

.app-sunset-long-text {
  margin: 16px 0 0 0;
  color: #646363;
  font-size: 16px;
  font-weight: 500;
  line-height: 160%;
  white-space: pre-line;
}

.app-sunset-more-questions {
  margin-top: 56px;

  @media (max-width: 600px) {
    margin-top: 80px;
  }
}

.app-sunset-whatsapp-button {
  max-width: 420px;
  margin: 32px auto 0 auto;
}

.app-sunset-whatsapp-button ::v-deep .new-btn {
  background-color: #0b5934 !important;
}
</style>
